import { Controller, FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { DialogFooter } from "components/ui/Dialog";
import { useAddPCRSCredentials } from "features/cdm/hooks/useAddPCRSCredentials";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "./PCRSCredentialsForm.module.css";

const formSchema = z.object({
  certificatePassword: z
    .string()
    .min(1, { message: "Please provide certificate password." }),
  pcrsUsername: z.string().min(1, { message: "Please provide PCRS username." }),
  pcrsPassword: z.string().min(1, { message: "Please provide PCRS password." }),
  vendorToken: z.string().min(1, { message: "Please provide vendor token." }),
  certificateFile: z.instanceof(File).optional().or(z.null()),
});

export const PCRSCredentialsForm = ({ initialValues, closeModal }) => {
  const { mutate: addPCRSCredentials, isPending } = useAddPCRSCredentials();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      certificatePassword: initialValues?.certificatePassword || "",
      pcrsUsername: initialValues?.pcrsUsername || "",
      pcrsPassword: initialValues?.pcrsPassword || "",
      vendorToken: initialValues?.vendorToken || "",
      certificateFile: null,
    },
  });

  const handleFormSubmit = ({
    certificatePassword,
    certificateFile,
    pcrsUsername,
    pcrsPassword,
    vendorToken,
  }) => {
    addPCRSCredentials(
      {
        certificatePassword,
        certificateFile,
        pcrsUsername,
        pcrsPassword,
        vendorToken,
      },
      {
        onSuccess: () => {
          toast.success("PCRS credentials saved successfully");
          closeModal();
        },
        onError: (error) => {
          toast.error("Failed to save PCRS credentials");
          console.error(error);
        },
      }
    );
  };

  return (
    <FormProvider {...form}>
      <form
        className={styles.form}
        onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("PCRSCredentialsForm"))}
      >
        <FormField name="pcrsUsername" label="PCRS Username">
          <input
            type="text"
            className={styles.input}
            {...form.register("pcrsUsername")}
            placeholder="Enter your PCRS username"
            autoComplete="off"
          />
        </FormField>

        <FormField name="pcrsPassword" label="PCRS Password">
          <input
            type="password"
            className={styles.input}
            {...form.register("pcrsPassword")}
            placeholder="Enter your PCRS password"
            autoComplete="off"
          />
        </FormField>

        <FormField name="vendorToken" label="Vendor Token">
          <input
            type="text"
            className={styles.input}
            {...form.register("vendorToken")}
            placeholder="Enter your vendor token"
            autoComplete="off"
          />
        </FormField>

        <FormField name="certificatePassword" label="Certificate Password">
          <input
            type="password"
            className={styles.input}
            {...form.register("certificatePassword")}
            placeholder="Enter certificate password"
            autoComplete="off"
          />
        </FormField>

        <FormField name="certificateFile" label="Certificate File">
          <Controller
            name="certificateFile"
            control={form.control}
            render={({ field: { onChange, ...field } }) => (
              <input
                type="file"
                className={`${styles.input} ${styles.fileInput}`}
                accept=".p12,.pfx,.pem,.crt"
                {...field}
                onChange={(e) => onChange(e.target.files?.[0] || null)}
              />
            )}
          />
        </FormField>

        <DialogFooter>
          <Button
            variant="outline"
            size="large"
            onClick={closeModal}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="large"
            type="submit"
            disabled={isPending}
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </form>
    </FormProvider>
  );
};
